<script setup lang="ts">
import { computed, ref, onMounted, watch, useTemplateRef, nextTick } from 'vue';
import moment from 'moment';
import { usePrimeVue } from 'primevue/config';
import { useI18n } from 'vue-i18n';
import type { FieldSchema } from '../../../types';
import type { AfDate } from '@Types';
import { getActivePrimeVueDateFormat, getShelterNow } from '@Constants';
import { getDefaultDateFormat, getParsedDate } from '@Helpers';
import { DAY_NAMES, DAY_NAMES_MIN, DAY_NAMES_SHORT, MONTHS, MONTHS_ABBR } from './constants';
import { useMobileDatePicker } from './useMobileDatePicker';
import usePresetDates from './usePresetDates';

const emits = defineEmits<{
  'update:modelValue': [Date | Date[] | null];
  onSelectInlineDate: [Date | Date[] | null];
}>();

const props = withDefaults(
  defineProps<{
    customInputClass?: string;
    dateFormat?: string;
    disabled?: boolean;
    disableDates?: Date[];
    disableDays?: number[];
    error?: string;
    field?: FieldSchema;
    fluid?: boolean;
    hasShortcutButtons?: boolean;
    hideOnDateTimeSelect?: boolean;
    hideOnRangeSelection?: boolean;
    hourFormat?: string;
    iconDisplay?: 'input' | 'button';
    isIntakeDate?: boolean;
    isOutcomeDate?: boolean;
    isInlineEditing?: boolean;
    manualInput?: boolean;
    maxDate?: Date;
    maxDateCount?: number;
    minDate?: Date;
    modelValue?: Date | Date[] | AfDate | string;
    noModelOnStart?: boolean;
    numberOfMonths?: number;
    removeClearIcon?: boolean;
    selectionMode?: 'multiple' | 'range' | 'single';
    showIcon?: boolean;
    showTime?: boolean;
    timeOnly?: boolean;
  }>(),
  {
    // NOTE :: No `dateFormat` default — `effectiveDateFormat` falls back to the SHELTER's
    // PrimeVue token. A hardcoded `'mm/dd/yy'` default here would win over the setting.
    hasShortcutButtons: false,
    hideOnDateTimeSelect: false,
    hideOnRangeSelection: true,
    hourFormat: '12',
    iconDisplay: 'input',
    isOutcomeDate: false,
    isInlineEditing: false,
    manualInput: true,
    noModelOnStart: false,
    numberOfMonths: 1,
    selectionMode: 'single',
    showIcon: true,
  }
);

const primeVue = usePrimeVue();
const { presetDates } = usePresetDates();
const { t, locale } = useI18n();

const inlineComponentRef = useTemplateRef<any>('calendarRef');

const calendarRef = ref();

const { isMobile, setupMobileInputClickHandler, openCalendarOverlay, calendarPassthroughProps } =
  useMobileDatePicker(calendarRef);

// NOTE :: Was a format→re-parse round trip through the US datetime format, purely to drop
// the seconds. Do it directly — the round trip broke the moment the format stopped being US.
// NOTE :: The SHELTER's now, not the browser's — this is the default every date picker in the
// app falls back to when its field has no model. See getShelterNow().
const currentDate = computed(() => moment(getShelterNow()).seconds(0).milliseconds(0).toDate());
const selectionMode = computed(() => props?.field?.settings?.selectionMode || props.selectionMode);
const settings = computed(() => props?.field?.settings);
const timeOnly = computed(() => props.timeOnly || props?.field?.settings?.timeOnly || false);
const isIntakeDate = computed(() => props.isIntakeDate || props?.field?.name === 'intake_date');
const isOutcomeDate = computed(() => props.isOutcomeDate || props?.field?.name === 'outcome_date');
const availableMonths = computed(() => {
  const DEFAULT = props.selectionMode === 'range' ? 2 : 1;

  return props?.field?.settings?.numberOfMonths || props.numberOfMonths || DEFAULT;
});
const calendarSelectionMode = computed(() => props?.field?.settings?.selectionMode || props.selectionMode);
const inputErrorClass = computed(() => [
  'text-sm text-gray-700 ' + props.customInputClass,
  { 'af-field-error': !!props.error },
]);
const _model = computed({
  get: () => {
    return (props.field?.settings.noModelOnStart || props.noModelOnStart) && !props.modelValue
      ? null
      : getSanitizedDate(props.modelValue);
  },
  set: (newSelection: Date | Date[] | null) => {
    emits('update:modelValue', newSelection);

    if (!props.isInlineEditing) return;
    if (isIntakeDate.value || isOutcomeDate.value) return;

    emits('onSelectInlineDate', newSelection);
  },
});
const shortCuts = computed(() =>
  presetDates.value.filter(({ selectionModes }) => selectionModes.includes(selectionMode.value))
);
const showShortcuts = computed(() => props?.field?.settings?.hasShortcutButtons || props.hasShortcutButtons);
const showTime = computed(() => timeOnly.value || props?.showTime || props?.field?.settings?.showTime || false);
const showSeconds = computed(() => timeOnly.value || props?.field?.settings?.showSeconds || false);
const effectiveDateFormat = computed(() =>
  timeOnly.value ? 'hh:mm:ss A' : settings?.value?.dateFormat || props.dateFormat || getActivePrimeVueDateFormat()
);

onMounted(() => handleMounted());

watch(locale, () => updatePrimeVueDatePickerLocale());
watch(() => props.modelValue, syncModelValueWhenEmpty);

function handleMounted() {
  updatePrimeVueDatePickerLocale();
  setInitialModel();
  setupMobileInputClickHandler();
}

function syncModelValueWhenEmpty(model: Date | Date[] | AfDate | string | undefined) {
  if (props.isInlineEditing) return;
  if (model) return;

  syncDefaultModelValue();
}

function syncDefaultModelValue() {
  if (props.isInlineEditing) return;

  const shouldClearModel = (props.field?.settings.noModelOnStart || props.noModelOnStart) && !props.modelValue;

  if (shouldClearModel) {
    emits('update:modelValue', null);
    return;
  }

  emits('update:modelValue', _model.value);
}

function setInitialModel() {
  checkEditType();
  syncDefaultModelValue();
}

function updatePrimeVueDatePickerLocale() {
  const { locale } = primeVue.config;
  if (!locale) return;

  locale.dayNames = DAY_NAMES.map((day) => t(`components.datePicker.dayNames.${day}`));
  locale.dayNamesMin = DAY_NAMES_MIN.map((day) => t(`components.datePicker.dayNamesMin.${day}`));
  locale.dayNamesShort = DAY_NAMES_SHORT.map((day) => t(`components.datePicker.dayNamesShort.${day}`));

  locale.monthNames = MONTHS.map((month) => t(`components.datePicker.months.${month}`));
  locale.monthNamesShort = MONTHS_ABBR.map((month) => t(`components.datePicker.monthsAbbr.${month}`));

  locale.am = 'AM';
  locale.pm = 'PM';
}

function getSanitizedDate(model: Date | Date[] | AfDate | string | undefined): Date | Date[] {
  const isUndefinedModel = !model;
  if (isUndefinedModel)
    return selectionMode.value === 'single' ? currentDate.value : [currentDate.value, currentDate.value];

  const isDateRange = Array.isArray(model);
  if (isDateRange) {
    const filteredDates = model.map((date) => getFormattedModel(date)).filter((date): date is Date => date !== null);
    return filteredDates.length > 0 ? filteredDates : [currentDate.value, currentDate.value];
  }

  const isSingleDate = moment.isDate(model);
  if (isSingleDate) {
    const formatted = getFormattedModel(model);
    return formatted ?? currentDate.value;
  }

  /**
   * NOTE
   * There is a possibility of receiving AfDate[]. This case is ommited
   * on purpose. So far, this moment that case never happened.
   */
  const formatted = getFormattedModel(model);
  return formatted ?? currentDate.value;
}

function getFormattedModel(model: Date | AfDate | string) {
  const sanitizedDate = getDefaultDateFormat(model, showTime.value, timeOnly.value);
  // NOTE: If the date is invalid, return null. The field will be empty.
  if (sanitizedDate === 'Invalid date') return null;

  // NOTE :: `sanitizedDate` is written in the SHELTER's format, so it has to be re-read with
  // the same tolerant list. A bare `moment(sanitizedDate)` returned an Invalid Date for every
  // non-US format and emptied the field with no error.
  return getParsedDate(sanitizedDate);
}

function checkEditType() {
  if (!props.isInlineEditing || !inlineComponentRef.value) return;

  nextTick(() => {
    const inputEl = inlineComponentRef.value?.$el?.querySelector('input');
    if (!inputEl) return;

    inputEl.focus();
  });
}

function onDataRangeSelection(value: Date | Date[]) {
  _model.value = value;
  calendarRef.value.overlayVisible = false;
}

function onClickOutsideDatePicker() {
  if (!isIntakeDate.value && !isOutcomeDate.value) return;

  emits('onSelectInlineDate', _model.value);
}
</script>

<template>
  <div class="flex">
    <!-- NOTE: As strange as this div is, this enables the placeholder of the Calendar component
     Without it the placeholder don't show up. -->
    <div v-if="!_model"></div>

    <div class="flex w-full items-center gap-2">
      <div class="min-w-0 flex-1">
        <Calendar
          v-model="_model"
          pt:root="h-9 w-full"
          ref="calendarRef"
          :dateFormat="effectiveDateFormat"
          :disableDates="settings?.disableDates || disableDates"
          :disableDays="settings?.disableDays || disableDays"
          :disabled="settings?.disabled || disabled"
          :fluid="settings?.fluid || fluid"
          :hideOnDateTimeSelect="settings?.hideOnDateTimeSelect || hideOnDateTimeSelect"
          :hideOnRangeSelection="settings?.hideOnRangeSelection || hideOnRangeSelection"
          :hourFormat="settings?.hourFormat || hourFormat"
          :iconDisplay="settings?.iconDisplay || iconDisplay"
          :invalid="!!error"
          :manualInput="settings?.manualInput || manualInput"
          :maxDate="settings?.maxDate || maxDate"
          :maxDateCount="settings?.maxDateCount || maxDateCount"
          :minDate="settings?.minDate || minDate"
          :numberOfMonths="availableMonths"
          :placeholder="$t('components.forms.placeholder.calendar')"
          :pt="calendarPassthroughProps"
          :pt:input:class="inputErrorClass"
          :pt:inputIcon:class="{ 'text-red-600': !!error }"
          :selectionMode="calendarSelectionMode"
          :showIcon="isMobile ? false : settings?.showIcon || showIcon"
          :showSeconds
          :showTime
          :timeOnly="settings?.timeOnly || timeOnly"
          @hide="onClickOutsideDatePicker"
        >
          <template
            v-if="showShortcuts"
            #footer
          >
            <Button
              v-for="{ label, value } in shortCuts"
              class="mr-2 mt-2 bg-brand-300 px-2 py-1 text-sm"
              :key="label"
              :label
              @click="onDataRangeSelection(value)"
            />
          </template>
        </Calendar>
      </div>

      <Button
        v-if="isMobile"
        outlined
        class="af-button w-9 border-none text-gray-400"
        icon="pi pi-calendar"
        severity="secondary"
        :disabled="settings?.disabled || disabled"
        @click.stop="openCalendarOverlay"
      />
    </div>

    <Button
      v-if="!removeClearIcon && (field?.settings?.noModelOnStart || noModelOnStart)"
      v-tooltip.top="{ value: $t('tooltip.eraseDate'), showDelay: 300 }"
      outlined
      class="af-button ml-1 w-9 border-none text-gray-400"
      icon="pi pi-eraser"
      severity="secondary"
      :disabled="settings?.disabled || disabled"
      @click="_model = null"
    />
  </div>
</template>

<style>
.p-datepicker-other-month {
  @apply opacity-30;
}
</style>
